"use client";

import { type ReactNode } from "react";
import { useTranslations } from "next-intl";
import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";
import { useToggleWithClickOutside } from "../../hooks/useToggleWithClickOutside";
import { Text, TextField } from "@sitecore-content-sdk/nextjs";

interface HorizontalNavigationToggleProps {
  id?: string;
  containerClass: string;
  scTitleField?: TextField;
  defaultExpanded?: boolean;
  children: ReactNode;
}

const HorizontalNavigationToggle = ({
  id,
  containerClass,
  scTitleField,
  defaultExpanded,
  children,
}: HorizontalNavigationToggleProps) => {
  const {
    isVisible: isExpanded,
    setIsVisible: setIsExpanded,
    ref,
  } = useToggleWithClickOutside<HTMLDivElement>(defaultExpanded);

  const t = useTranslations();
  const title = t.has("horizontal-navigation-title")
    ? t("horizontal-navigation-title")
    : "Navigation";

  const onToggle = () => setIsExpanded((v) => !v);

  return (
    <div
      ref={ref}
      className={containerClass}
      data-role="horizontal-navigation-component"
    >
      <div className="px-4 py-8 @lg/nav:py-16">
        <div
          className={`bg-white border border-lightning-blue ${
            isExpanded ? "rounded-t-lg" : "rounded-lg"
          } shadow-[var(--horizontal-nav-shadow)]`}
          id={id ?? undefined}
          data-testid="horizontal-nav-component"
        >
          <button
            type="button"
            className={`w-full flex justify-between cursor-pointer items-center p-4`}
            onClick={onToggle}
            aria-expanded={isExpanded}
          >
            <Text
              field={scTitleField ?? { value: title }}
              className={
                "m-0 text-dark-black text-base leading-[1.5] font-medium lg:text-[24px] lg:leading-[1.25] lg:font-normal"
              }
              tag="h4"
              data-testid="horizontal-nav-title"
            />
            <Icon
              icon={ICONS.CHEVRON_BLUE_DOWN}
              className={`transition-transform duration-300 ease-out h-6 w-6 max-h-6 ${
                isExpanded ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>
          {/* grid-rows 0fr -> 1fr trick animates smoothly from 0 to the
              content's natural height without an arbitrary max-height that
              causes lag and overshoot. */}
          <div
            className={`grid transition-[grid-template-rows] duration-300 ease-out bg-neptune-neutral-300 ${
              isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
            data-testid="accordion-body"
            aria-hidden={!isExpanded}
          >
            <div className="overflow-hidden">
              <div className="flex w-full h-full p-4 lg:p-6">
                <ul
                  className="bg-transparent flex max-w-full flex-grow-1 flex-wrap gap-2 md:gap-6 flex-row"
                  data-testid="list-items"
                >
                  {children}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HorizontalNavigationToggle;
